import React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartBox } from "./ReportViz";

import type { GooglePace } from "../hooks/useGoogleReport";
import { campaignStack, stackEdges, type StackInput, type StackRow } from "../utils/campaignStack";
import { addDays, axisTicks, formatShortDay, rangeDays, type DayRange } from "../utils/dayAxis";
import type { CampaignLane } from "../utils/dayMarkers";
import { CampaignLanes } from "./CampaignLanes";
import { StackLegend, StackTooltip } from "./CampaignStackParts";
import { DayPips, type DayPip } from "./DayPips";

interface PaceChartProps {
  pace: GooglePace;
  /** The headline metric, plural: "actions", "sessions", "clicks". */
  label: string;
  /** This period's line, in the source's colour. */
  color: string;
  /** This period's days: the month, or the window. */
  range: DayRange;
  /** The days before, overlaid by offset (day 0 on day 0); null when the
   *  period before is not compared. */
  previousRange: DayRange | null;
  /** The two lines' names: "Oct" and "Sep", or the window's. `before`, when
   *  given, is the label's short word for the period before, the legend
   *  keeping both names: a window reads "384 vs 362 before". */
  labels: { current: string; previous: string; before?: string };
  /** A complete period's label sets the two whole periods side by side, as
   *  its tiles do; a month in progress compares the same days. */
  complete: boolean;
  /** The headline metric over the same-days windows, the pair the tiles
   *  compare; the label of a month in progress reads it. */
  toDate: { through: string; current: number | null; previous: number | null } | null;
  /** The marked days, pips on the x axis at this period's days. */
  pips?: ReadonlyArray<DayPip>;
  /** This period's campaigns, each its measure by day: the running total is
   *  drawn as a stack of their running totals in place of the line, the
   *  period before's dashed total on top. Without a daily row, the line. */
  campaigns?: ReadonlyArray<StackInput>;
  /** Campaign lanes under the axis, indexed over `range`. */
  lanes?: { lanes: ReadonlyArray<CampaignLane>; hidden: number };
}

interface PaceRow {
  /** The day's place on the axis, from 0. */
  index: number;
  /** This period's day at the index. */
  date: string;
  current: number | null;
  previous: number | null;
  stack: StackRow | null;
}

// Last month is the reference, so it stays grey and dashed behind this
// month's line; the dash keeps the two apart without colour.
const PREVIOUS_COLOR = "#9CA3AF";
const AXIS_COLOR = "#E5E7EB";
const GRID_COLOR = "#F3F4F6";
const TICK = { fontSize: 10, fill: "#9CA3AF" };
// The pips sit on the plot's x axis line, so they read these two.
const Y_AXIS_WIDTH = 32;
const X_AXIS_HEIGHT = 30;

const formatCount = (n: number | null): string => (n == null ? "-" : n.toLocaleString());

const formatTick = (n: number): string =>
  n >= 1_000 ? `${Math.round(n / 100) / 10}K` : String(n);

/** The period's running total by day against the period before's, on one
 *  axis of day indexes, the period before overlaid by offset: the gap
 *  between the lines on a day is the same-days comparison. This period's
 *  line stops at the source's settled day. The axis is as long as the
 *  longer of the two, so 30 days of a month set against 31 keep their
 *  places. */
export const PaceChart: React.FC<PaceChartProps> = ({
  pace,
  label,
  color,
  range,
  previousRange,
  labels,
  complete,
  toDate,
  pips,
  campaigns,
  lanes,
}) => {
  if (pace.current.length === 0 && pace.previous.length === 0) return null;

  const currentDays = rangeDays(range);
  const previousDays = previousRange ? rangeDays(previousRange) : [];
  const length = Math.max(currentDays.length, previousDays.length);
  const axis: DayRange = { since: range.since, until: addDays(range.since, length - 1) };
  const current = new Map(pace.current.map((p) => [p.date, p.value]));
  const previous = new Map(pace.previous.map((p) => [p.date, p.value]));
  // Before any settled day, the day before the period: nothing is drawn.
  const settled = pace.current[pace.current.length - 1]?.date ?? addDays(range.since, -1);
  const stack = campaigns
    ? campaignStack(campaigns, { range: axis, cumulative: true, through: settled })
    : null;
  const indexRow = (index: number, stackRow: StackRow | null): PaceRow => {
    const date = addDays(range.since, index);
    const previousDate = previousDays[index];
    return {
      index,
      date,
      current: current.get(date) ?? null,
      previous: previousDate ? previous.get(previousDate) ?? null : null,
      stack: stackRow,
    };
  };
  const rows: PaceRow[] = Array.from({ length }, (_, i) => indexRow(i, stack?.rows[i] ?? null));
  // The chart also draws the rows that square a campaign's start and end.
  const chartRows = stack ? stackEdges(stack.rows).map((s) => indexRow(s.index, s)) : rows;
  // The last tick reads this period's own last day.
  const ticks = axisTicks(length).map((t, i, all) => (i === all.length - 1 ? currentDays.length - 1 : t));

  // The direct label: on a complete month the two whole months; in progress
  // the same-days pair the tiles compare, never the pace endpoints, which in
  // late March would set 30 days against February's 28.
  const latest = pace.current[pace.current.length - 1];
  const previousTotal = pace.previous[pace.previous.length - 1]?.value ?? null;
  const throughDay = toDate ? Number(toDate.through.slice(8, 10)) : null;
  const thisPeriod = labels.current;
  const lastPeriod = labels.previous;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5 gap-2">
        <span className="text-[12px] font-medium text-gray-500">
          {label}, running total
        </span>
        <span className="text-[12px] text-gray-500 tabular-nums truncate">
          {latest && complete ? (
            <>
              <span className="font-medium text-gray-900">{latest.value.toLocaleString()}</span>
              {/* Not compared: the number alone, no "vs -"; the subtitle says why. */}
              {!previousRange
                ? null
                : labels.before
                ? ` vs ${formatCount(previousTotal)} ${labels.before}`
                : ` ${thisPeriod} vs ${formatCount(previousTotal)} ${lastPeriod}`}
            </>
          ) : !complete && toDate ? (
            <>
              <span className="font-medium text-gray-900">{formatCount(toDate.current)}</span>
              {" vs "}
              {formatCount(toDate.previous)} by day {throughDay}
            </>
          ) : (
            "no settled days yet"
          )}
        </span>
      </div>
      <div className="flex items-center gap-3 mb-1 text-[11px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 border-t-2" style={{ borderColor: color }} />
          {thisPeriod}
        </span>
        {previousRange && (
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3 border-t-2 border-dashed"
              style={{ borderColor: PREVIOUS_COLOR }}
            />
            {lastPeriod}
          </span>
        )}
      </div>
      <ChartBox height={168} className="relative">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartRows} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis
              dataKey="index"
              type="number"
              domain={[0, length - 1]}
              ticks={ticks}
              tickFormatter={(index: number) => formatShortDay(addDays(range.since, index))}
              height={X_AXIS_HEIGHT}
              tick={TICK}
              tickLine={false}
              axisLine={{ stroke: AXIS_COLOR }}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              tickCount={3}
              tick={TICK}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickFormatter={formatTick}
            />
            <RechartsTooltip
              cursor={{ stroke: AXIS_COLOR }}
              content={({ active, label: index }) => {
                const row = active ? rows[Number(index)] : undefined;
                if (row && stack && row.stack && row.stack.total != null) {
                  return (
                    <StackTooltip
                      row={row.stack}
                      layers={stack.layers}
                      format={(n) => n.toLocaleString()}
                      heading={formatShortDay(row.date)}
                      totalLabel={thisPeriod}
                      footer={
                        <div className="flex justify-between gap-2 text-gray-300">
                          <span>{lastPeriod}</span>
                          <span>{formatCount(row.previous)}</span>
                        </div>
                      }
                    />
                  );
                }
                return row ? (
                  <div className="rounded-md bg-gray-900 text-white text-[11px] px-2 py-1 shadow-md tabular-nums">
                    <span className="text-gray-300">{formatShortDay(row.date)}:</span>{" "}
                    <span className="font-medium">{formatCount(row.current)}</span>
                    <span className="text-gray-300"> vs {formatCount(row.previous)}</span>
                  </div>
                ) : null;
              }}
            />
            {stack ? (
              stack.layers.map((layer) => (
                <Area
                  key={layer.key}
                  dataKey={(row: PaceRow) => row.stack?.values[layer.key] ?? null}
                  name={layer.name}
                  stackId="campaigns"
                  type="linear"
                  stroke="#FFFFFF"
                  strokeWidth={1.5}
                  fill={layer.color}
                  fillOpacity={1}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              ))
            ) : (
              <Line
                dataKey="current"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 4, stroke: "#FFFFFF", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            )}
            {/* After this period's areas, so the line before stays on top. */}
            <Line
              dataKey="previous"
              stroke={PREVIOUS_COLOR}
              strokeWidth={2}
              strokeDasharray="4 3"
              strokeLinecap="round"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        {pips && pips.length > 0 && (
          <DayPips
            pips={pips}
            range={axis}
            domain={[0, length - 1]}
            inset={{ left: Y_AXIS_WIDTH, right: 4, bottom: X_AXIS_HEIGHT }}
          />
        )}
      </ChartBox>
      {lanes && (
        <CampaignLanes
          lanes={lanes.lanes}
          hidden={lanes.hidden}
          length={length}
          inset={{ left: Y_AXIS_WIDTH, right: 4 }}
        />
      )}
      {stack && <StackLegend layers={stack.layers} format={(n) => n.toLocaleString()} />}
    </div>
  );
};
